import axios, { AxiosError } from "axios";
import axiosInstance from "config/axiosConfig";

interface Address {
  addressLineOne: string;
  addressLineTwo: string;
  city: string;
  state: string;
  postalCode: string;
}

interface LeadCreatePayload {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  address: Address;
  referral: string;
  channel: string;
}

interface Source {
  referral: string;
  channel: string;
}

export interface Lead {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  emailAddress: string;
  source: Source;
}

export const API_PATHS = {
  LEAD_CREATE: "/contact/",
  LEAD_LIST: "/contact/",
  LEAD_SEARCH: "/contact/search",
  LEAD_PAGINATED: "/contact",
};

export type ErrorType = {
  error: string;
  message: string;
  status: number;
  timestamp: string;
};

// Handle errors to extract backend error messages
const handleError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorType>;
    return axiosError.response?.data?.message || "An unknown error occurred";
  }
  return "An unknown error occurred";
};

// Create a lead
const createLead = async (leadData: LeadCreatePayload): Promise<Lead> => {
  try {
    const response = await axiosInstance.post(API_PATHS.LEAD_CREATE, leadData);
    return response.data;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

// Get list of leads
const getLeads = async (): Promise<Lead[]> => {
  try {
    const response = await axiosInstance.get(API_PATHS.LEAD_LIST);
    return response.data;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

// Search leads by last name or phone number
const searchLeads = async (
  lastName?: string,
  phoneNumber?: number
): Promise<Lead[]> => {
  try {
    const response = await axiosInstance.get(API_PATHS.LEAD_SEARCH, {
      params: { lastName, phoneNumber },
    });
    return response.data;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

// Get paginated leads
const getPaginatedLeads = async (
  start: number = 1,
  end: number = 500
): Promise<Lead[]> => {
  try {
    const response = await axiosInstance.get(API_PATHS.LEAD_PAGINATED, {
      params: { start, end },
    });
    return response.data;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

// Check if a lead exists by phone number
const checkLeadExists = async (phoneNumber: string): Promise<Lead | null> => {
  try {
    const response = await axiosInstance.get(API_PATHS.LEAD_SEARCH, {
      params: { phoneNumber },
    });
    const leads: Lead[] = response.data;
    return leads.length > 0 ? leads[0] : null;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

export {
  createLead,
  getLeads,
  searchLeads,
  getPaginatedLeads,
  checkLeadExists,
};
